<template>
    <div class="mt-2" id="event-detail">
        <loading :active='isLoading'/>
        
        <h3 class="mt-3 mb-4 bg-secondary p-2 rounded border">{{ event.EventName }}</h3>


        <div id="event-status-bar" class="bg-dark-blue text-light px-3 py-1 rounded fw-light mb-3" style="font-size: small;">
            <div class="row">
                <div class="col-12 col-md-6 text-center text-md-start">
                    <span class="me-2">Open until</span><span class="fw-500 me-2">{{ event.CloseDate }}</span>&bull;
                    <span id="event-status" class="fw-500 ms-1" :status="`${ event._Status.string }`">{{ event._Status.string }}</span>
                </div>
                <div class="col-12 col-md-6 text-center text-md-end">
                    <span class="me-1">Remaining seats</span><span class="mx-1 fw-500">{{ event.RemainingSeats }}</span>&nbsp;of&nbsp;<span class="ms-1 fw-500">{{ event.MaxSeats }}</span>
                </div>
            </div>
        </div>
 

        <div class="card mb-4">
            <div class="card-header h5">Description</div>
            <div class="card-body">
                <div class="card-text">
                    <pre-formatted :content="event.EventDesc"></pre-formatted>
                </div>
            </div>
            <div class="card-footer">
                <event-detail-contact 
                    :contact-name="event.ContactName" 
                    :event-id="event.EventID"
                    :cf-turnstile-site-key="event._CfTurnstileKey"
                ></event-detail-contact>
                <div>
                    <a v-if="event.Flyer" :href="`/event/${event.EventID}/flyer`" target="_blank"><i class="fas fa-file-pdf me-2 text-center" style="width: 1em;"></i>Flyer</a>
                </div>
                <div v-if="event.ContactHours" class="text-dark-blue">
                    <i class="fas fa-certificate me-2" style="width: 1em;"></i><span>Contact Hours:</span><span class="ms-2 fw-500 text-center">{{ event.ContactHours }}</span>
                </div>
            </div>
        </div>

        <div class="card mb-4">
            <div class="card-header h5">Time and Location</div>
            <div class="card-body">
                <div class="row mt-3">
                    <div class="mb-4 col-12 col-lg-5">
                        <h6>Start Time</h6>
                        <div>{{ event._TimesFormatted.Start }}</div>
                        
                        <h6 class="mt-2">End Time</h6>
                        <div>{{ event._TimesFormatted.End }}</div>
                    </div>
                    
                    <div class="col-12 col-lg-7">
                        <!--
                        Don't show location information if no address/city/state is populated 
                        (e.g. Online events)
                        -->
                        <div v-if="event._LocationDetails.Address && event._LocationDetails.City && event._LocationDetails.State" class="row">
                            <!-- <h6 class="col-12 mb-1">Location</h6> -->
                            <!-- <div class="col-12 col-md-6 col-lg-5 mb-3"> -->
                                <!-- <iframe id="map-frame" frameborder="0" :src="`https://www.google.com/maps/embed/v1/place?q=${locString}&key=${event._GoogleMapsApiKey}`" allowfullscreen ></iframe> -->
                                 <!-- <leaflet-map></leaflet-map> -->
                            <!-- </div> -->

                            <div class="col-12 col-md-6 col-lg-7">
                                <div class="fw-500 text-dark-blue">{{ event._LocationDetails.Name }}</div>
                                <div>
                                    {{ event._LocationDetails.Address }}<br/>
                                    {{ event._LocationDetails.City }}, {{ event._LocationDetails.State }}&nbsp;&nbsp;{{ event._LocationDetails.Zip }}<br/>
                                    <a class="" :href="`/location/${event._LocationDetails.LocationGUID}`" target="_blank"><i class="fas fa-external-link-square-alt me-2"></i>View in Google Maps</a>
                                </div>
                            </div>
                        </div>
                        <span v-else class="fw-500 text-dark-blue">{{ event._LocationDetails.Name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <!-- EVENT FEE -->
            <div class="mb-4 col-12 col-md-6 col-lg-6">
                <event-detail-fees :fees="event._Fees" :member="event._Member"></event-detail-fees>
            </div>

            <!-- ONLINE EVENT -->
            <div class="mb-4 col-12 col-lg-6">
                <div v-if="event.OnlineEventID" class="card">
                    <div class="card-header h5">Online Event</div>
                    <div class="card-body">
                        <div class="mb-4">
                            <h6 class="d-inline me-2">Event URL:</h6><a :href="url(event.OnlineEventURL)" target="_blank">{{ event.OnlineEventURL }}</a>
                        </div>
                        <!-- <div style="white-space: pre;" :html="textHtml(event.OnlineInstructions)"></div> -->
                        <pre-formatted :content="event.OnlineInstructions"></pre-formatted>
                    </div>
                </div>
            </div>
        </div>
    
        <!-- REGISTRANTS -->
        <div v-if="event.ShowRegistrations && !event.ExternalRegistration" id="registrants" class="detail-block col-12 col-lg-6">
            <registrants :registrants="event._Registrants"></registrants>
        </div>



        <!-- SIGN UP BUTTONS -->
        <div class="mb-5 mt-5 text-center">
            <span v-if="event.ExternalRegistration" id="external-link-backer" class="border rounded p-3 d-inline-block">
                <button v-if="event._Status.string == 'ACTIVE'" class="btn btn-warning" @click="external">
                    <i class="fas fa-user-check me-2"></i>Sign Up</button>
                <button v-else class="btn btn-warning" disabled><i class="fas fa-user-check me-2"></i>Sign Up</button>
                <span class="d-block">External Link</span>
            </span>
            <button v-else-if="event._Status.string == 'ACTIVE'" class="btn btn-primary" 
                @click="enroll"><i class="fas fa-user-check me-2"></i>Sign Up</button>
            <button v-else class="btn btn-primary" disabled><i class="fas fa-user-check me-2"></i>Sign Up</button>
        </div>
    </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import Registrants from './components/Registrants.vue';
import PreFormatted from './components/PreFormatted.vue';
import EventDetailContact from './components/EventDetailContact.vue';
import EventDetailFees from './components/EventDetailFees.vue';

// import LeafletMap from './components/LeafletMap.vue';

/**
 * TODO
 * - Better way to get Google API key from server
 * - Better way to get Turnstile API key from server
 */

export default {
    components: { 
        Loading, 
        Registrants, 
        PreFormatted,
        EventDetailFees,
        EventDetailContact, 
        //LeafletMap,
    },

    data() {
        return {
            isLoading: false,
            event: {
                _LocationDetails: {},
                _Status: {},
                _Registrants: [],
                _TimesFormatted: {},
                _Member: false,
                _Fees: {},
                id: 0,
            },
            // googleMapsKey: 'AIzaSyBboFlgoNTvrdq57iR6HIqEdxCbSM0YNcA',
            // turnstileKey: '',
        }
    },

    computed: {
        locString: function() {
            const loc = this.event._LocationDetails;
            let location = `${loc.Name},${loc.Address},${loc.City},${loc.State},${loc.Zip}`;
            return encodeURIComponent(location);
        }
    },

    mounted() {
        this.isLoading = true;
        axios
            .get(`/api/event/${this.$route.params.event}`)
            .then(response => {
                this.event = response.data.data;
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                this.isLoading = false;
            })
    },

    methods: {

        url: function(text='') {
            if (text.match(/^https?:\/\//)) {
                return text;
            } else {
                return `https://${text}`;
            }
        },

        enroll: function() {
            this.$router.push(`/event/${this.event.EventID}/enroll`);
        },

        external: function() {
            window.open(this.event.ExternalRegistrationURL);
        },
    },
}
</script>

<style scoped>

#event-detail #event-status[status=ACTIVE] {
  color: LightGreen;
}
#event-detail #event-status[status=CLOSED] {
  color: LightSkyBlue;
}
#event-detail #event-status[status=CANCELLED] {
  color: LightSalmon;
}
#event-detail #event-status[status=INACTIVE] {
  color: lightcoral;
}
#event-detail #event-status[status=FULL] {
  color: lightcyan;
}
#event-detail #event-status[status=UNKNOWN] {
  color: violet;
}
#event-detail #map-frame {
  border: 1px solid #ccc;
  max-width: inherit;
}

#external-link-backer {
  border: 1px solid DarkBlue !important;
  background-color: #f0fdff;
  color: DarkBlue;
}
#external-link-backer span {
  font-size: small;
}

</style>